import { checkSSOSession, getAccessToken, logout } from "./keycloak"

import React, { lazy, Suspense, useEffect, useMemo, useState, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import {
  Alert,
  AppBar,
  Box,
  Button,
  CircularProgress,
  createTheme,
  DialogContent,
  Divider,
  Fab,
  LinearProgress,
  MenuItem,
  MenuList,
  Paper,
  Popover,
  Snackbar,
  styled,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material"

import {
  AddRounded,
  ChevronRightRounded,
  DeleteRounded,
  ErrorOutlineRounded,
  MailOutlineRounded,
  KeyboardArrowUpRounded,
  LogoutRounded,
  PersonRounded,
  TranslateRounded
} from "@mui/icons-material"

import HKVAnalytics from "./components/HKVAnalytics/1.0"
import HKVDialog from "./components/HKVDialog/7.0"
import HKVLink from "./components/HKVLink"
import { DialogFooter, DialogHeader } from "./components/DialogHeader"

import Draggable from "react-draggable"

import {
  Link as RouterLink,
  Navigate,
  Route,
  Routes,
  useLocation
} from "react-router-dom"

// Pagina's worden per route geladen. Zonder dit staat alles in één bundel: wie
// het uploadformulier opent haalt ook echarts binnen (de wereldkaart op de
// homepage, 1,1 MB) en wie de homepage opent de hele kaartstack (Leaflet +
// esri-leaflet + maplibre, ~0,9 MB). Nu betaalt elke route alleen voor zichzelf.
const About = lazy(() => import("./pages/About"))
const Account = lazy(() => import("./pages/Account"))
const Catalog = lazy(() => import("./pages/Catalog"))
const CatalogSpecies = lazy(() => import("./pages/Catalog/Species"))
const DiveDeep = lazy(() => import("./pages/DiveDeep"))
const Home = lazy(() => import("./pages/Home"))
const Partners = lazy(() => import("./pages/Partners"))
const Terms = lazy(() => import("./pages/Terms"))

const AdminAiModel = lazy(() => import("./pages/Admin/AiModel"))
const AdminAiModelRun = lazy(() => import("./pages/Admin/AiModelRun"))
const AdminAiModelTrainingData = lazy(() => import("./pages/Admin/AiModelTrainingData"))
const AdminUsers = lazy(() => import("./pages/Admin/Users"))
const AnalyseBatch = lazy(() => import("./pages/Observations/Analyse/Batch"))
const AnalysePrediction = lazy(() => import("./pages/Observations/Analyse/Prediction"))
const AnalyseSpecialist = lazy(() => import("./pages/Observations/Analyse/Specialist"))
const AnalyseViewObservation = lazy(() => import("./pages/Observations/Analyse/ViewObservation"))
const EditObservation = lazy(() => import("./pages/Observations/Upload/EditObservation"))
const UploadAddTank = lazy(() => import("./pages/Observations/Upload/AddTank"))
const UploadSpecialist = lazy(() => import("./pages/Observations/Upload/Specialist"))

import ScrollToTop from "./components/ScrollToTop"
import { MUI_LOCALES } from "./lib/muiLocales"
import { getSpecialistColor, getSpecialistIcon } from "./lib/specialistTheme"
import { Seo } from "./components/Seo"
import { AuthGuard } from "./components/AuthGuard"

import i18n from "./i18n"

import moment from "moment"
// Alleen Nederlands: `moment/min/locales` laadt alle 137 locales (369 KB
// onverkleind) terwijl de app er drie talen op nahoudt. Engels zit in de kern
// van moment; voor `pa` bestaat geen locale (moment kent alleen `pa-in`,
// Punjabi), dus die viel al terug op Engels en doet dat nog steeds.
import "moment/locale/nl"

import "./App.css"

import { clearErrors, removeNotification } from "./store/reducers/errors"
import { addLocalStorage } from "./store/reducers/localStorage"

import { getDisclaimer, getSupportEmail } from "./store/environment"
import { buildErrorReportMailto } from "./lib/errorReport"

import { DesktopMenu, Footer, MobileMenu } from "./components/StyledComponents/"

const StyledNav = styled("div", {
})(({ theme }) => {
  return {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    background: "transparent",

    maxWidth: "100%",
    width: 1200,
    margin: "auto",

    padding: theme.spacing(1, 2), boxSizing: "border-box"
  }
})

const StyledLanguageContainer = styled("div", {
})(({ theme }) => {
  return {
    display: "flex", alignItems: "center", cursor: "pointer",
    [theme.breakpoints.down(1000)]: {
      "& .language-text": {
        display: "none"
      }
    }
  }
})

const StyledPopoverItem = styled("div", {
})(({ theme }) => {
  return {
    display: "flex",
    alignItems: "center",
    margin: theme.spacing(0, -2),
    padding: theme.spacing(0.5, 2),
    color: "inherit !important",
    textDecoration: "none",
    "&:hover": {
      background: theme.palette.action.hover
    }
  }
})

const StyledFooterContainer = styled("div", {
})(({ theme }) => {
  return {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: theme.spacing(4),
    margin: theme.spacing(2, 0),
    "& .footer-item": {
      display: "flex", flexDirection: "column", gap: theme.spacing(1),
      width: "100%",
      padding: theme.spacing(0, 2)
    },
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      "& .footer-item": {
        width: "100%"
      }
    }
  }
})

function App() {
  const dispatch = useDispatch()
  const theme = useTheme()
  const { t } = useTranslation()

  const accountInfo = useSelector((state) => state.accountInfo)
  const specialists = useSelector((state) => state.specialists)
  const errors = useSelector((state) => state.errors)
  const notifications = useSelector((state) => state.notifications)
  const loader = useSelector((state) => state.loader)
  const localStorage = useSelector((state) => state.localStorage)

  const location = useLocation()

  const [initialized, setInitialized] = useState(false)

  const webServerRef = useRef()

  const languages = [
    { short: "nl", long: "Nederlands", path: "netherland" },
    { short: "pa", long: "Papiaments", path: "papiaments" },
    { short: "en", long: "English", path: "united_kingdom" }
  ]
  const [selectedLanguage, setSelectedLanguage] = useState(localStorage.selectedLanguage
    ? localStorage.selectedLanguage
    : "en"
  )
  const [languageSelector, setLanguageSelector] = useState({ state: false, anchor: 0 })
  const localizedTheme = useMemo(() => createTheme(theme, ...MUI_LOCALES[selectedLanguage]), [theme, selectedLanguage])

  const [dialogConfigs, setDialogConfigs] = useState({})

  const [showScrollFab, setShowScrollFab] = useState(false)
  // De snelkoppeling naar een uploadformulier: welke rol wil je indienen?
  const [uploadAnchor, setUploadAnchor] = useState(null)
  const [scrolledPast, setScrolledPast] = useState(false)

  // De uploadrollen die deze gebruiker heeft. Heeft hij er geen, dan is er niets te beginnen en is
  // de knop er ook niet.
  const uploadSpecialists = useMemo(() => Object.values(specialists || {})
    .filter(meta => meta.upload && accountInfo?.roles?.includes(meta.role)), [specialists, accountInfo])

  const menuItems = useMemo(() => {
    const items = [
      { label: "Home", path: "" },
      { label: "About_us", path: "about" },
      { label: "Dive_deep", path: "dive-deep" },
      {
        label: "Partners", path: null,
        menuItems: [
          { label: "Diver", path: "partners/diver" },
          { label: "Fisher", path: "partners/fisher" },
          { label: "Aquarist", path: "partners/aquarist" },
          { label: "Researcher", path: "partners/scientist" }
        ]
      }
    ]

    // Het overzicht van waarnemingen en de catalogus zijn er alleen voor wie ze mag inzien; hun
    // routes staan om dezelfde reden achter een `AuthGuard`. `path: null` maakt "Observations" zelf
    // geen link, net als bij Partners: er is geen pagina die alle rollen tegelijk toont.
    const canBrowse = ["admin", "analyzer"].some(role => accountInfo?.roles?.includes(role))
    // De catalogus heeft zijn eigen rol: `admin` en `analyzer` geven er geen toegang meer op.
    const canViewCatalog = accountInfo?.roles?.includes("catalog-viewer")
    const browsable = Object.entries(specialists || {}).filter(([, meta]) => meta.upload)

    if (canBrowse && browsable.length > 0) {
      items.push({
        label: "Observations", path: null,
        menuItems: browsable.map(([role, meta]) => ({
          label: meta.title,
          path: `observations/view/${role}`
        }))
      })
    }

    if (canViewCatalog) items.push({ label: "Catalog", path: "catalog" })

    return items
  }, [accountInfo, specialists])

  const [isHome, setIsHome] = useState(location.pathname === "/")

  const [partners, setPartners] = useState([
    { name: "Blijdorp", image: "blijdorp.png", url: "https://diergaardeblijdorp.nl/en" },
    { name: "HKV", image: "hkv.png", url: "https://www.hkv.nl/en" },
    { name: "Dive Friends Bonaire", image: "dive_friends_bonaire.png", url: "https://www.divefriendsbonaire.com" },
    { name: "Eaza", image: "eaza.png", url: "https://www.eaza.net" },
    { name: "Piskabon", image: "piskabon.png", url: "https://www.facebook.com/piskabon" },
    { name: "Stinapa", image: "stinapa.png", url: "https://stinapabonaire.org" },
    { name: "WWF", image: "wwf.png", url: "https://www.worldwildlife.org" },
    { name: "EUAC", image: "euac.png", url: "https://www.euac.org" }
  ])

  useEffect(() => {
    dispatch(checkSSOSession())
      .then(() => setInitialized(true))

    const handleScroll = () => {
      const triggerHeight = window.innerHeight // * 1.5 // 150vh
      setShowScrollFab(window.scrollY > triggerHeight)

      if (!isHome) {
        setScrolledPast(false)
        return
      }

      const appBar = document.getElementById("header")
      const threshold = appBar?.offsetHeight ?? 64

      setScrolledPast(window.scrollY > threshold)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [isHome])

  useEffect(() => {
    // Chrome/Edge treat mouse-wheel-over-a-focused-<input type="number"> as one atomic native action: it
    // changes the value AND doesn't scroll the page. That's easy to trigger by accident and tends to leave
    // the field at 0/-1. We can't selectively keep only the "scroll the page" half via preventDefault or
    // blur alone, so: suppress the native behavior entirely (no value change, focus stays put) and replay
    // the scroll ourselves so the page still moves exactly as if the cursor weren't over the field.
    const handleWheel = (e) => {
      if (e.target?.tagName === "INPUT" && e.target.type === "number") {
        e.preventDefault()
        window.scrollBy({ top: e.deltaY, left: e.deltaX })
      }
    }

    document.addEventListener("wheel", handleWheel, { passive: false })
    return () => document.removeEventListener("wheel", handleWheel)
  }, [])

  // useEffect(() => {
  //   if (!accountInfo?.expires) return

  //   const expiresAt = moment(accountInfo.expires)
  //   const now = moment()

  //   const refreshTime = expiresAt.subtract(2, "minutes")
  //   const msUntilRefresh = refreshTime.diff(now, "milliseconds")

  //   if (msUntilRefresh <= 0) {
  //     dispatch(getAccessToken())
  //     return
  //   }

  //   const timer = setTimeout(() => {
  //     dispatch(getAccessToken())
  //   }, msUntilRefresh)

  //   return () => clearTimeout(timer)
  // }, [accountInfo?.expires])

  useEffect(() => {
    setIsHome(location.pathname === "/")
  }, [location.pathname])

  useEffect(() => {
    i18n.changeLanguage(selectedLanguage)
    moment.locale(selectedLanguage)

    dispatch(addLocalStorage({ key: "selectedLanguage", value: selectedLanguage }))
  }, [selectedLanguage])

  const openDialog = (key, size, options) => {
    const mergedOptions = { ...options, open: true, size: size ? size : "lg" }
    setDialogConfigs(prevState => ({
      ...prevState,
      [key]: mergedOptions
    }))
  }

  const closeDialog = (key) => {
    setDialogConfigs(prevState => {
      const { [key]: _, ...rest } = prevState
      return rest
    })
  }

  const renderLoader = () => {
    if (loader > 0) {
      return (
        <div className="loader-background">
          <div className="loader-default">
            <LinearProgress sx={{ width: 300 }} />
          </div>
        </div>
      )
    } else return null
  }

  const renderWebServerMsg = () => {
    const disclaimer = getDisclaimer()
    if (!disclaimer) return null

    return (
      <Draggable bounds="parent" nodeRef={webServerRef}>
        <Snackbar
          ref={webServerRef}
          open={true}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          sx={{ zIndex: 99999999, cursor: "grab" }}
        >
          <Tooltip title={t("Drag_me")}>
            <Alert elevation={1} severity="info" sx={{ padding: theme.spacing(0, 1) }}>
              {t(disclaimer)}
            </Alert>
          </Tooltip>
        </Snackbar>
      </Draggable>
    )
  }

  // De gebruiker krijgt alleen de melding te zien. Tijdstip, functienaam en foutcode zeggen hem
  // niets en maakten het scherm alleen drukker; ze gaan mee in het foutrapport, waar ze wel
  // gelezen worden. Zie lib/errorReport.js.
  const renderErrors = () => {
    const single = errors.length === 1

    return (
      <HKVDialog
        config={{ open: errors.length > 0, size: "sm" }}
        onClose={() => dispatch(clearErrors())}
      >
        <DialogHeader
          icon={<ErrorOutlineRounded />}
          title={single ? t("Error_title_one") : t("Error_title_other", { count: errors.length })}
          color="error"
          onClose={() => dispatch(clearErrors())}
        />
        <DialogContent sx={{ display: "flex", flexDirection: "column", maxWidth: "100%", maxHeight: 400 }}>
          {/* Alleen de meldingen. De kop is al rood met een icoon; dat per melding herhalen zegt
              niets extra en maakt van een enkele fout een alarm. Een scheidslijn is genoeg om te
              zien waar de ene ophoudt en de volgende begint. */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, overflow: "auto" }}>
            {errors.map((error, index) => (
              <React.Fragment key={index}>
                {index > 0 && <Divider />}
                <Typography variant="body2">{t(error.message)}</Typography>
              </React.Fragment>
            ))}
          </Box>
        </DialogContent>
        <DialogFooter>
          <Button
            variant="outlined"
            startIcon={<MailOutlineRounded />}
            component="a"
            href={buildErrorReportMailto(errors, getSupportEmail(), {
              url: window.location.href,
              userAgent: window.navigator.userAgent
            })}
          >
            {single ? t("Report_error_one") : t("Report_error_other")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => dispatch(clearErrors())}
          >
            {t("Ok_label")}
          </Button>
        </DialogFooter>
      </HKVDialog>
    )
  }

  const closeNotification = (reason, index) => {
    if (reason === "clickaway") return null
    dispatch(removeNotification({ index }))
  }

  const renderNotifications = () => {
    const height = 50
    return notifications.map((notification, index) => {
      const bottom = (height * index)

      return (
        <Snackbar
          disableWindowBlurListener
          key={`${notification.key}-${index}`}
          open={true}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          autoHideDuration={notification.duration ? notification.duration : 4000}
          onClose={(event, reason) => closeNotification(reason, index)}
          onClick={() => closeNotification("click", index)}
          sx={{
            zIndex: 99999999,
            width: "fit-content",
            maxWidth: `calc(100% - ${theme.spacing(2)})`,
            height: height,
            left: 8,
            right: 8,
            margin: "0 auto",
            bottom: `calc(${bottom}px + ${theme.spacing(1 * (index + 1))}) !important`
          }}
        >
          <Alert elevation={2} severity={notification.type}>
            <Typography className="ellipsis">
              {notification.message.toString()}
            </Typography>
          </Alert>
        </Snackbar>
      )
    })
  }

  const renderAccountInfo = () => {
    if (accountInfo?.token) {
      return (
        <HKVLink
          to="/account"
          title="Account"
        >
          {/* Donkerder bij hover, net als elke andere knop: het is er een, ook al is het een vlak
              binnen een link. */}
          <Box
            sx={{
              background: theme.palette.primary.main,
              display: "flex", alignItems: "center", gap: 1,
              borderRadius: "8px",
              padding: theme.spacing(1, 2, 1, 1),
              color: "white",
              transition: theme.transitions.create("background-color"),
              "&:hover": { background: theme.palette.primary.dark }
            }}
          >
            <PersonRounded />
            {/* Een lange naam of een e-mailadres duwde het menu ernaast in de verdrukking. Hier
                afkappen in plaats van de rest laten wijken; de volledige naam staat op /account. */}
            <Typography
              noWrap
              variant={isHome ? "body1" : "body2"}
              title={accountInfo?.name || accountInfo?.email}
              sx={{ fontWeight: 500, maxWidth: { xs: 120, lg: 220 } }}
            >
              {accountInfo?.name || accountInfo?.email}
            </Typography>
          </Box>
        </HKVLink>
      );
    } else {
      return (
        <div
          style={{
            background: theme.palette.primary.main,
            display: "flex", alignItems: "center", gap: theme.spacing(1),
            borderRadius: 8,
            padding: theme.spacing(1, 2),
            color: "white"
          }}
        >
          <Typography
            variant={isHome ? "body1" : "body2"}
            onClick={() => dispatch(getAccessToken())}
            sx={{
              fontWeight: 500,
              cursor: "pointer"
            }}>
            {t("Log_in")}
          </Typography>
          <Fab
            color="secondary"
            variant="extended"
            size="small"
            onClick={() => dispatch(getAccessToken())}
            sx={{ boxShadow: "unset", marginLeft: theme.spacing(1) }}
          >
            <Typography variant={isHome ? "body1" : "body2"} sx={{
              fontWeight: 500
            }}>{t("Sign_up")}</Typography>
            <ChevronRightRounded />
          </Fab>
        </div>
      );
    }
  }

  const renderDeleteDialog = () => {
    if (!dialogConfigs.delete) return null

    const {
      callback,
      title,
      content
    } = dialogConfigs.delete

    return (
      <HKVDialog
        config={dialogConfigs.delete}
        onClose={() => {
          closeDialog("delete")
          callback(false)
        }}
      >
        <DialogHeader
          icon={<DeleteRounded />}
          title={title}
          color="error"
          onClose={() => {
            closeDialog("delete")
            callback(false)
          }}
        />
        <DialogContent sx={{ flexDirection: "column" }}>
          {content}
        </DialogContent>
        <DialogFooter>
          <Button
            color="inherit"
            variant="outlined"
            onClick={() => {
              closeDialog("delete")
              callback(false)
            }}
          >
            {t("Cancel_label")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              closeDialog("delete")
              callback(true)
            }}
          >
            {t("Delete_label")}
          </Button>
        </DialogFooter>
      </HKVDialog>
    )
  }

  const renderSignoutDialog = () => {
    if (!dialogConfigs.signout) return null

    return (
      <HKVDialog config={dialogConfigs.signout} onClose={() => closeDialog("signout")}>
        <DialogHeader icon={<LogoutRounded />} title={t("Log_out_title")} onClose={() => closeDialog("signout")} />
        {/* De vraag staat in de kop; hieronder alleen de knoppen, in de balk die elke dialoog heeft. */}
        <DialogFooter>
          <Button
            color="inherit"
            variant="outlined"
            onClick={() => closeDialog("signout")}
          >
            {t("Cancel_label")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              closeDialog("signout")
              logout()
            }}
          >
            {t("Log_out")}
          </Button>
        </DialogFooter>
      </HKVDialog>
    )
  }

  const toggleLngSelect = (event) => {
    setLanguageSelector(prevState => ({
      state: !prevState.state,
      anchor: event?.currentTarget ?? prevState.anchor
    }))
  }

  const renderLanguageSelector = () => {
    const currentLanguage = languages.find(l => l.short === selectedLanguage)
    if (!currentLanguage) return null

    return (
      <div style={{ display: "flex", margin: theme.spacing(1), padding: "2px 7px" }}>
        <Tooltip title={t("Select_language")} placement="right">
          <StyledLanguageContainer
            className="ellipsis"
            onClick={toggleLngSelect}
          >
            <TranslateRounded />
            {Boolean(open) && <Typography
              className="language-text"
              sx={{ margin: theme.spacing(0, 0, 0, 0.5) }}
              variant={isHome ? "body1" : "body2"}
            >
              {currentLanguage.long}
            </Typography>}
          </StyledLanguageContainer>
        </Tooltip>
        <Popover
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          open={languageSelector.state}
          anchorEl={languageSelector.anchor}
          onClose={toggleLngSelect}
          anchorOrigin={{
            horizontal: "right",
            vertical: "top"
          }}
        >
          <Paper sx={{ padding: theme.spacing(1, 2) }}>
            {languages.map((language, index) => (
              <StyledPopoverItem
                key={index}
                className="transition"
                style={{
                  background: selectedLanguage === language.short && theme.palette.action.selected,
                  cursor: "pointer"
                }}
                onClick={(event) => {
                  setSelectedLanguage(language.short)
                  toggleLngSelect(event)
                }}
              >
                <Typography>{language.long}</Typography>
              </StyledPopoverItem>
            ))}
          </Paper>
        </Popover>
      </div>
    )
  }

  if (!initialized) {
    return (
      <div className="App" id="app">
        {renderLoader()}
        {renderErrors()}
        {renderNotifications()}
        {renderWebServerMsg()}
        <HKVAnalytics />
      </div>
    )
  }

  const page = (
    <div className="App" id="app">
      {renderLoader()}
      {renderErrors()}
      {renderNotifications()}
      {renderWebServerMsg()}
      <HKVAnalytics />
      <AppBar
        id="header"
        elevation={isHome && !scrolledPast ? 0 : 1}
        position={isHome ? "fixed" : "relative"}
        color={isHome && !scrolledPast ? "transparent" : "inherit"}
        sx={{
          zIndex: 3,
          transition: "none",//"background-color 0.15s ease, box-shadow 0.15s ease",
        }}
      >
        <StyledNav>
          <MobileMenu menuItems={menuItems} />
          <DesktopMenu isHome={isHome && !scrolledPast} menuItems={menuItems} />
          {/* De knop hangt onder de accountnaam en lijnt rechts met dat blok uit: het is de snelste
              weg naar een formulier, en die hoort bij "wie ben ik" en niet in een hoek van het
              scherm. Absoluut gepositioneerd zodat hij de hoogte van de header niet verandert. */}
          <div style={{ position: "relative", display: "flex", alignItems: "center", gap: theme.spacing(1) }}>
            {renderLanguageSelector()}
            {renderAccountInfo()}
            {uploadSpecialists.length > 0 && (
              <Fab
                variant="extended"
                color="secondary"
                size="small"
                sx={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  zIndex: 4,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  boxShadow: theme.shadows[4],
                  // MUI geeft een uitgeklapte fab links 16 en rechts 12 pixels; met een icoon links
                  // valt dat verschil op. Hier andersom, want een icoon oogt luchtiger dan tekst.
                  pl: 1.5,
                  pr: 2
                }}
                onClick={(event) => setUploadAnchor(event.currentTarget)}
              >
                <AddRounded fontSize="small" sx={{ mr: 0.75 }} />
                {t("New_observation_label")}
              </Fab>
            )}
          </div>
        </StyledNav>
      </AppBar>
      {/* `main` groeit mee (zie App.css), zodat de footer altijd onderaan het scherm staat en niet
          vlak onder een korte pagina. */}
      <main>
      <Suspense fallback={
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <CircularProgress />
        </Box>
      }>
      <Routes>
        <Route
          path="/"
          element={<Home />}
        />
        <Route
          path="/about"
          element={<About
            selectedLanguage={selectedLanguage}
          />}
        />
        {accountInfo && <Route
          path="/account"
          element={<Account
            beforeSignOut={() => openDialog("signout", "sm")}
            beforeDelete={(options) => openDialog("delete", "sm", options)}
          />}
        />}
        {/* View, browsing observations (admin/analyzer overview list, and single-observation view) */}
        <Route
          path="/observations/view/:specialist"
          element={<AuthGuard roles={["admin", "analyzer"]} action="browse" shell>
            <AnalyseSpecialist
              beforeDelete={(options) => openDialog("delete", "sm", options)}
              selectedLanguage={selectedLanguage}
            />
          </AuthGuard>}
        />
        {/* View a single observation, reachable from the overview above (admin/analyzer) and from Account (the owner) */}
        <Route
          path="/observations/view/:specialist/:upload_id"
          element={<AnalyseViewObservation selectedLanguage={selectedLanguage} />}
        />
        {/* Analyse, actually running/reviewing an AI analysis on one attachment (aquarist/scientist only) */}
        <Route
          path="/observations/analyse/:specialist/:id/:upload_id"
          element={<AuthGuard roles={["admin", "analyzer"]} action="analyse" shell><AnalysePrediction /></AuthGuard>}
        />
        {/* Dezelfde analyse, maar over een selectie foto's. De API heeft daar geen endpoint voor,
            dus de pagina roept `/predict` per foto aan. */}
        <Route
          path="/observations/analyse/:specialist/batch"
          element={<AuthGuard roles={["admin", "analyzer"]} action="analyse" shell><AnalyseBatch /></AuthGuard>}
        />
        {/* Upload */}
        <Route
          path="/observations/upload/:specialist/:upload_id?"
          element={<UploadSpecialist selectedLanguage={selectedLanguage} />}
        />
        <Route
          path="/observations/edit/:specialist/:upload_id"
          element={<EditObservation selectedLanguage={selectedLanguage} />}
        />
        <Route
          path="/observations/upload/:specialist/add_tank"
          element={<UploadAddTank selectedLanguage={selectedLanguage} />}
        />
        <Route
          path="/observations/upload/:specialist/edit_tank/:tank_id"
          element={<UploadAddTank selectedLanguage={selectedLanguage} />}
        />

        {/* De catalogus is nu een eigen rol. Achter een `AuthGuard` en niet weggelaten uit de
            routes: wie een link krijgt hoort te lezen dat hij de rol mist, en niet op de
            homepagina te belanden zonder uitleg. */}
        <Route
          path="/catalog"
          element={<AuthGuard roles={["catalog-viewer"]} action="view" shell><Catalog /></AuthGuard>}
        />
        <Route
          path="/catalog/:species_id"
          element={<AuthGuard roles={["catalog-viewer"]} action="view" shell><CatalogSpecies /></AuthGuard>}
        />
        {/* Gebruikersbeheer hangt aan de clientrollen van `realm-management`, waar de Keycloak
            Admin API zelf op controleert, en niet aan een rol van deze app: het scherm praat
            rechtstreeks met Keycloak. `manage-users` is de eis, want dat is wat rollen zetten
            vraagt; wie het rolpakket "Gebruikersbeheer" heeft, heeft hem. Lezen gebeurt met
            `view-users` en `query-users`; `view-realm` gebruikt dit scherm bewust niet. */}
        <Route
          path="/admin/users"
          element={<AuthGuard clientRoles={{ client: "realm-management", roles: ["manage-users"] }} action="manage_users" shell>
            <AdminUsers />
          </AuthGuard>}
        />
        {/* Dezelfde pagina, met het rollendialoog van deze gebruiker open, zodat er naar te linken
            is. */}
        <Route
          path="/admin/users/:user_id"
          element={<AuthGuard clientRoles={{ client: "realm-management", roles: ["manage-users"] }} action="manage_users" shell>
            <AdminUsers />
          </AuthGuard>}
        />
        {/* Het soortmodel opnieuw laten trainen, beoordelen en activeren. Hangt aan de rol `admin`
            van deze app, want de `/ml`-endpoints doen dat ook (`require_role("admin")`). Zie
            api/eggspedition_modelhertraining_plan.md, deel C. */}
        <Route
          path="/admin/ai-model"
          element={<AuthGuard roles={["admin"]} action="manage_model" shell><AdminAiModel /></AuthGuard>}
        />
        <Route
          path="/admin/ai-model/runs/:run_id"
          element={<AuthGuard roles={["admin"]} action="manage_model" shell><AdminAiModelRun /></AuthGuard>}
        />
        <Route
          path="/admin/ai-model/versions/:model_version_id"
          element={<AuthGuard roles={["admin"]} action="manage_model" shell><AdminAiModelRun /></AuthGuard>}
        />
        <Route
          path="/admin/ai-model/training-data"
          element={<AuthGuard roles={["admin"]} action="manage_model" shell><AdminAiModelTrainingData /></AuthGuard>}
        />
        <Route path="/dive-deep" element={<DiveDeep
          selectedLanguage={selectedLanguage}
        />} />
        <Route path="/partners/:specialist" element={<Partners />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="*" element={<Navigate replace to="/" />} />
      </Routes>
      </Suspense>
      </main>
      <Footer>
        <StyledFooterContainer sx={{ justifyContent: "center" }}>
          <div className="footer-item" style={{ alignItems: "center", textAlign: "center" }}>
            <Typography variant="h6" sx={{
              fontWeight: 500
            }}>{t("Powered_by")}</Typography>
            <div style={{ display: "flex", gap: theme.spacing(1), flexWrap: "wrap", justifyContent: "center" }}>
              {partners.map((partner, ix) => (
                <HKVLink
                  key={ix}
                  title={partner.name}
                  to={partner.url}
                >
                  <div
                    style={{
                      display: "flex",
                      width: 120, height: 100,
                      border: `1px solid ${theme.palette.divider}`, borderRadius: theme.shape.borderRadius,
                      padding: theme.spacing(0.5), boxSizing: "border-box"
                    }}
                  >
                    <img
                      src={`/static/images/partners/${partner.image}`}
                      alt={partner.name}
                      width="100%" height="100%"
                      style={{ objectFit: "contain" }}
                    />
                  </div>
                </HKVLink>
              ))}
            </div>
          </div>
        </StyledFooterContainer>
      </Footer>
      {showScrollFab && (
        <Tooltip title={t("Scroll_up")}>
          <Fab
            sx={{
              position: "fixed",
              bottom: theme.spacing(4),
              right: theme.spacing(4),
              zIndex: 9999,
              boxShadow: theme.shadows[3]
            }}
            size="small"
            color="primary"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <KeyboardArrowUpRounded />
          </Fab>
        </Tooltip>
      )}
      {/* Naar beneden uitklappend, met de rechterkant onder de knop. */}
      <Popover
        open={Boolean(uploadAnchor)}
        anchorEl={uploadAnchor}
        onClose={() => setUploadAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { mt: 1, minWidth: 240 } } }}
      >
        <MenuList sx={{ py: 0.5 }}>
          {uploadSpecialists.map(meta => {
            const Icon = getSpecialistIcon(meta.role)
            const color = getSpecialistColor(theme, meta.role)

            return (
              <MenuItem
                key={meta.role}
                component={RouterLink}
                to={`/observations/upload/${meta.role}`}
                onClick={() => setUploadAnchor(null)}
                sx={{ gap: 1.5, py: 1 }}
              >
                <Icon fontSize="small" sx={{ color: color.main }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{t(meta.title)}</Typography>
              </MenuItem>
            )
          })}
        </MenuList>
      </Popover>
      <ScrollToTop />
      <Seo language={selectedLanguage} />
      {renderDeleteDialog()}
      {renderSignoutDialog()}
    </div>
  )

  // De standaardteksten van MUI in de gekozen taal, ook in alles wat hieronder opent.
  return <ThemeProvider theme={localizedTheme}>{page}</ThemeProvider>
}

export default App
